import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Paper,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Stack,
  Checkbox,
  FormControlLabel,
  Divider
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  LocalShipping as ContainerIcon,
  LocalShipping as LocalShippingIcon,
  Receipt as ReceiptIcon,
  Today as TodayIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Upload as UploadIcon,
  GetApp as DownloadIcon,
  Home as HomeIcon,
  NotificationsActive as AlertIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  AdminPanelSettings as AdminIcon,
  Lock as LockIcon,
  CallSplit as CallSplitIcon,
  History as HistoryIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { api, API_URL } from '../config/api';
import { getAlertsSummary, getAlerts, generateAlerts, type AlertSummary, type Alert as POAlert } from '../services/alertsService';
import ContainerDashboard from './ContainerDashboard';
import ContainerDetailModal from './ContainerDetailModal';
import POReconciliation from './POReconciliation';
import ContainerNav from './ContainerNav';
import PackingListUploadModal from './PackingListUploadModal';
import ManualContainerDialog from './ManualContainerDialog';

interface Container {
  id: number;
  container_no: string;
  seal_no?: string;
  delivery_day?: string;
  delivery_time?: string;
  dock?: string;
  location?: string;  // Optional - only in grouped view
  status?: 'in_transit' | 'received';
  chain_of_custody?: string | null;
  line_count?: number;  // For multi-PO: line count for THIS PO only (from grouped view)
  stock_codes_preview?: string;  // For multi-PO: stock codes for THIS PO only (optional - grouped view only)
  is_priority?: boolean;
  priority_comment?: string;
  priority_created_by?: string;  // Optional - only in grouped view
  priority_created_at?: string;  // Optional - only in grouped view
  // New fields for multi-PO system
  is_shared?: boolean;  // True if container has multiple POs
  po_numbers?: string[];  // All POs in this container (from detail view)
  primary_po_number?: string | null;  // Primary PO (most lines)
  po_summary?: Array<{  // Breakdown per PO (from detail view)
    po_number: string;
    line_count: number;
    unique_stocks: number;
  }>;
}

interface POGroup {
  po_number: string;
  supplier_code: string;
  demurrage_deadline: string;
  delivery_day: string;
  total_lines: number;
  has_containers: boolean;
  import_status: string; // 'active', 'pending_review', or 'legacy'
  priority_containers: Container[];
  standard_containers: Container[];
  total_containers: number;
  priority_count: number;
  due_diligence_completed: boolean;
}

interface POLine {
  po_number: string;
  line_number: number;
  supplier_code: string;
  stock_code: string;
  description: string;
  chain_of_custody: string | null;
  ordered_qty: number;
  invoiced_qty: number;
  unit: string;
  expected_delivery_by: string | null;
  requested_delivery_by: string | null;
  intended_customer: string;
  import_status: string;
  imported_at: string | null;
  packing_list_qty: number;
}

interface ImportHistory {
  filename: string;
  import_date: string;
  import_type: string;
  lines_count: number;
  lines_matched?: number;
  lines_unmatched?: number;
  processing_status?: string;
  container_no?: string;
}

interface Priority {
  priority_id: number;
  comment: string;
  created_by: string;
  created_at: string;
  container_id: number;
  container_no: string;
  po_number: string;
  status: string;
  dock: string;
  delivery_day: string;
  line_count: number;
}

interface ContainerLine {
  id: number;
  stock_code: string;
  po_number: string | null;  // NEW: PO for this specific line (multi-PO system)
  rolls_per_pallet: number;
  sheets: number;
  metres: number;
  prepopulated_qty: number;
  net_weight_tonnes: number;
  linear_length_m: number;
}

const ContainerManagementEnhanced: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [poGroups, setPOGroups] = useState<POGroup[]>([]);
  const [supplierCodes, setSupplierCodes] = useState<string[]>([]); // Options for the supplier filter
  const [priorities, setPriorities] = useState<Priority[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState(''); // Search applied on blur
  const [expanded, setExpanded] = useState<string[]>([]);
  const [expandedContainers, setExpandedContainers] = useState<number[]>([]);
  const [expandedPOLines, setExpandedPOLines] = useState<string[]>([]);
  const [expandedAlerts, setExpandedAlerts] = useState<string[]>([]);
  const [containerLines, setContainerLines] = useState<Record<number, ContainerLine[]>>({});
  const [editingContainer, setEditingContainer] = useState<number | null>(null);
  const [editDeliveryData, setEditDeliveryData] = useState<{delivery_day: string, delivery_time: string, seal_no: string, dock: string, chain_of_custody: string}>({delivery_day: '', delivery_time: '', seal_no: '', dock: '', chain_of_custody: ''});
  const [editingPODemurrage, setEditingPODemurrage] = useState<string | null>(null);
  const [editDemurrageData, setEditDemurrageData] = useState('');
  const [priorityModalOpen, setPriorityModalOpen] = useState(false);
  const [selectedContainer, setSelectedContainer] = useState<Container | null>(null);
  const [priorityComment, setPriorityComment] = useState('');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState<any>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState<any>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [updatingPOStatus, setUpdatingPOStatus] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    date: '',
    dock: '',
    supplier: ''
  });
  const [expandedPO, setExpandedPO] = useState<string | null>(null);
  const [poLines, setPOLines] = useState<{[key: string]: POLine[]}>({});
  const [loadingPOLines, setLoadingPOLines] = useState<{[key: string]: boolean}>({});
  const [packingListFile, setPackingListFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: {current: number, total: number}}>({});
  const [alertsSummary, setAlertsSummary] = useState<AlertSummary | null>(null);
  const [poAlerts, setPOAlerts] = useState<{[key: string]: POAlert[]}>({});
  const [allAlerts, setAllAlerts] = useState<POAlert[]>([]);
  const [scanningAlerts, setScanningAlerts] = useState(false);
  const [uploadingPackingList, setUploadingPackingList] = useState<string | null>(null);
  const [uploadingAdditionalPackingList, setUploadingAdditionalPackingList] = useState<string | null>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  // PO Reconciliation summary stats
  const [poSummaryStats, setPOSummaryStats] = useState({
    total: 0,
    noPackingList: 0,
    mismatch: 0,
    match: 0
  });

  // Container detail modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedContainerForDetail, setSelectedContainerForDetail] = useState<Container | null>(null);
  const [importHistory, setImportHistory] = useState<{[key: string]: {open_order_imports: ImportHistory[], packing_list_imports: ImportHistory[]}}>({});
  const [loadingImportHistory, setLoadingImportHistory] = useState<{[key: string]: boolean}>({});
  const [expandedImportHistory, setExpandedImportHistory] = useState<string[]>([]);
  const [sealUpdateText, setSealUpdateText] = useState('');
  const [bulkSealUpdating, setBulkSealUpdating] = useState(false);
  const [bulkSealResults, setBulkSealResults] = useState<any>(null);
  const [removeContainerNo, setRemoveContainerNo] = useState('');
  const [removeContainerPreview, setRemoveContainerPreview] = useState<any>(null);
  const [removeContainerLoading, setRemoveContainerLoading] = useState(false);
  const [removeContainerConfirmOpen, setRemoveContainerConfirmOpen] = useState(false);
  const [removeContainerForceConfirmOpen, setRemoveContainerForceConfirmOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [bulkSealModalOpen, setBulkSealModalOpen] = useState(false);
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [alertsModalOpen, setAlertsModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'by-po' | 'by-packing-list'>('by-po');
  const [selectedPOForAlerts, setSelectedPOForAlerts] = useState<string | null>(null);
  const [importManagementModalOpen, setImportManagementModalOpen] = useState(false);
  const [selectedPOForImports, setSelectedPOForImports] = useState<string | null>(null);
  const [manualContainerDialogOpen, setManualContainerDialogOpen] = useState(false);

  // Use API_URL which includes /api path

  // Dock options
  const dockOptions = [
    { value: 'Dock 1', label: 'Dock 1 (Building 3)' },
    { value: 'Dock 2', label: 'Dock 2 (Building 4)' },
    { value: 'Dock 3', label: 'Dock 3 (Building 2)' },
    { value: 'Dock 4', label: 'Dock 4 (Building 1)' },
    { value: 'Dock 5', label: 'Dock 5 (Building 5)' }
  ];

  const getDockDisplayName = (dockValue: string) => {
    const dock = dockOptions.find(d => d.value === dockValue);
    return dock ? dock.label : dockValue;
  };

  const getStatusColor = (status?: string): 'default' | 'primary' | 'secondary' | 'success' => {
    switch (status) {
      case 'in_transit': return 'primary';
      case 'received': return 'success';
      default: return 'default';
    }
  };

  // Handler for PO Reconciliation data updates
  const handlePODataLoad = (summaries: any[]) => {
    setPOSummaryStats({
      total: summaries.length,
      noPackingList: summaries.filter(po => po.total_packing_list_qty === 0).length,
      mismatch: summaries.filter(po => po.total_packing_list_qty > 0 && po.total_ordered_qty !== po.total_packing_list_qty).length,
      match: summaries.filter(po => po.total_ordered_qty === po.total_packing_list_qty).length
    });
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      // Load grouped containers, priorities, and alerts in parallel
      await Promise.all([
        loadGroupedContainers(),
        loadPriorities(),
        loadAlertsSummary()
      ]);

    } catch (err) {
      setError('Error loading container data');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, appliedSearchTerm]);

  const loadAlertsSummary = async () => {
    try {
      const [summary, alertsData] = await Promise.all([
        getAlertsSummary(),
        getAlerts({ status: 'open', limit: 1000 })
      ]);
      setAlertsSummary(summary);
      setAllAlerts(alertsData.alerts || []);
    } catch (err) {
      console.error('Error loading alerts summary:', err);
    }
  };

  const loadPOAlerts = async (poNumber: string) => {
    try {
      // Filter from already loaded alerts
      const filteredAlerts = allAlerts.filter(alert => alert.po_number === poNumber);
      setPOAlerts(prev => ({ ...prev, [poNumber]: filteredAlerts }));
    } catch (err) {
      console.error('Error loading PO alerts:', err);
    }
  };

  const getPOAlertCount = (poNumber: string): number => {
    return allAlerts.filter(alert => alert.po_number === poNumber).length;
  };

  // Load all unfiltered PO groups for dropdown options
  // Just the supplier codes. This used to load /containers-grouped unfiltered
  // — 167 PO groups, 483 KB — and reduce it to a Set purely to fill a dropdown,
  // running the page's heaviest query a second time on every mount.
  const loadSupplierCodes = useCallback(async () => {
    try {
      const response = await api.get(`/containers-grouped?suppliers_only=1`);
      if (response.data.success) {
        setSupplierCodes(response.data.supplier_codes || []);
      }
    } catch (err) {
      console.error('Error loading supplier codes:', err);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load the filter's options once on mount
  useEffect(() => {
    loadSupplierCodes();
  }, [loadSupplierCodes]);

  const loadGroupedContainers = async () => {
    const params = new URLSearchParams();
    if (filters.date) params.append('date', filters.date);
    if (filters.dock) params.append('dock', filters.dock);
    if (filters.supplier) params.append('supplier', filters.supplier);
    if (appliedSearchTerm) params.append('search', appliedSearchTerm);

    const response = await api.get(`/containers-grouped?${params}`);
    if (response.data.success) {
      setPOGroups(response.data.po_groups);

      // Auto-expand POs with priority containers
      const priorityPOs = response.data.po_groups
        .filter((po: POGroup) => po.priority_count > 0)
        .map((po: POGroup) => po.po_number);
      setExpanded(priorityPOs);
    }
  };

  const loadPriorities = async () => {
    const response = await api.get(`/containers-priorities`);
    if (response.data.success) {
      setPriorities(response.data.priorities);
    }
  };

  const loadContainerLines = async (containerId: number) => {
    try {
      const response = await api.get(`/containers/${containerId}/lines`);
      if (response.data.success) {
        setContainerLines(prev => ({
          ...prev,
          [containerId]: response.data.lines
        }));
      }
    } catch (err) {
      console.error('Error loading container lines:', err);
    }
  };

  const loadPOLines = async (poNumber: string) => {
    try {
      setLoadingPOLines(prev => ({ ...prev, [poNumber]: true }));
      const response = await api.get(`/po-lines/${poNumber}`);
      if (response.data.success) {
        setPOLines(prev => ({
          ...prev,
          [poNumber]: response.data.lines
        }));
      }
    } catch (err) {
      console.error('Error loading PO lines:', err);
    } finally {
      setLoadingPOLines(prev => ({ ...prev, [poNumber]: false }));
    }
  };

  const loadImportHistory = async (poNumber: string) => {
    try {
      setLoadingImportHistory(prev => ({ ...prev, [poNumber]: true }));
      const response = await api.get(`/po-import-history/${poNumber}`);
      if (response.data.success) {
        setImportHistory(prev => ({
          ...prev,
          [poNumber]: {
            open_order_imports: response.data.open_order_imports,
            packing_list_imports: response.data.packing_list_imports
          }
        }));
      }
    } catch (err) {
      console.error('Error loading import history:', err);
    } finally {
      setLoadingImportHistory(prev => ({ ...prev, [poNumber]: false }));
    }
  };

  const handlePackingListUpload = async (poNumber: string, files: FileList) => {
    const fileArray = Array.from(files);
    const totalFiles = fileArray.length;

    try {
      setUploadingPackingList(poNumber);
      setUploadProgress({ [poNumber]: { current: 0, total: totalFiles } });

      const results = [];

      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];

        // Update progress
        setUploadProgress({ [poNumber]: { current: i + 1, total: totalFiles } });

        // Use auto-creation mode (container_id = "1") to let the packing list processor create containers
        const formData = new FormData();
        formData.append('file', file);
        // Include staff_no so the import is properly attributed
        if (user?.staff_no) {
          formData.append('staff_no', user.staff_no);
        }

        try {
          const packingResponse = await api.post(
            '/containers/process-packing-list-v2',
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );

          if (packingResponse.data.success) {
            results.push({
              filename: file.name,
              success: true,
              data: packingResponse.data
            });
          } else {
            results.push({
              filename: file.name,
              success: false,
              error: packingResponse.data.error
            });
          }
        } catch (fileErr: any) {
          results.push({
            filename: file.name,
            success: false,
            error: fileErr.response?.data?.error || fileErr.message
          });
        }
      }

      // Refresh the data to show the new container(s)
      loadData();
      setPackingListFile(null);

      // Show summary of all uploads
      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);

      let message = `Uploaded ${successful.length} of ${totalFiles} packing list(s)!\n\n`;

      if (successful.length > 0) {
        message += '✓ Successful uploads:\n';
        successful.forEach(r => {
          const result = r.data;
          message += `  • ${r.filename}\n`;
          message += `    Supplier: ${result.supplier}, PO: ${result.po_number}\n`;
          message += `    Containers: ${result.containers_created}, Pallets: ${result.total_pallets_processed} (${result.pallets_matched} matched)\n`;
        });
      }

      if (failed.length > 0) {
        message += '\n✗ Failed uploads:\n';
        failed.forEach(r => {
          message += `  • ${r.filename}: ${r.error}\n`;
        });
      }

      alert(message);

    } catch (err: any) {
      console.error('Error uploading packing lists:', err);
      alert(`Error uploading packing lists: ${err.message}`);
    } finally {
      setUploadingPackingList(null);
      setUploadProgress({});
    }
  };

  const handleAdditionalPackingListUpload = async (poNumber: string, file: File) => {
    try {
      setUploadingAdditionalPackingList(poNumber);

      // Use auto-creation mode (container_id = "1") to let the packing list processor create additional containers
      const formData = new FormData();
      formData.append('file', file);
      // Include staff_no so the import is properly attributed
      if (user?.staff_no) {
        formData.append('staff_no', user.staff_no);
      }

      const packingResponse = await api.post(
        '/containers/process-packing-list-v2',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (packingResponse.data.success) {
        // Refresh the data to show the new container(s)
        loadData();

        // Show detailed success message with debugging info
        const result = packingResponse.data;
        console.log('Additional packing list processing result:', result);
        alert(`Additional packing list uploaded successfully!

New containers created: ${result.containers_created}
Lines processed: ${result.processed_count}
Lines matched: ${result.matched_count}
Container lines created: ${result.container_lines_created}
Total rows in file: ${result.total_rows}

These containers have been added to the existing PO and will appear in the list above.`);
      } else {
        throw new Error(packingResponse.data.error || 'Failed to process additional packing list');
      }

    } catch (err: any) {
      console.error('Error uploading additional packing list:', err);
      console.error('Error response:', err.response?.data);
      alert(`Error uploading additional packing list: ${err.response?.data?.error || err.message}`);
    } finally {
      setUploadingAdditionalPackingList(null);
    }
  };

  const handleContainerExpand = (containerId: number) => {
    if (expandedContainers.includes(containerId)) {
      setExpandedContainers(prev => prev.filter(id => id !== containerId));
    } else {
      setExpandedContainers(prev => [...prev, containerId]);
      // Load container lines if not already loaded
      if (!containerLines[containerId]) {
        loadContainerLines(containerId);
      }
    }
  };

  const handlePriorityToggle = async (container: Container) => {
    if (container.is_priority) {
      // Remove from priority
      try {
        await api.delete(`/containers/${container.id}/priority`);
        loadData(); // Refresh data
      } catch (err) {
        setError('Error removing priority');
      }
    } else {
      // Add to priority
      setSelectedContainer(container);
      setPriorityComment('');
      setPriorityModalOpen(true);
    }
  };

  const handlePrioritySubmit = async () => {
    if (!selectedContainer || !priorityComment.trim()) {
      setError('Priority comment is required');
      return;
    }

    try {
      await api.post(`/containers/${selectedContainer.id}/priority`, {
        comment: priorityComment.trim(),
        created_by: user?.staff_no || 'MATERIALS_TEAM'
      });

      setPriorityModalOpen(false);
      setSelectedContainer(null);
      setPriorityComment('');
      loadData(); // Refresh data
    } catch (err) {
      setError('Error adding priority');
    }
  };

  const handleAccordionChange = (po_number: string) => {
    const isExpanding = !expanded.includes(po_number);

    setExpanded(prev =>
      prev.includes(po_number)
        ? prev.filter(po => po !== po_number)
        : [...prev, po_number]
    );

    // Load PO lines and import history when expanding and not already loaded
    if (isExpanding) {
      if (!poLines[po_number]) {
        loadPOLines(po_number);
      }
      if (!importHistory[po_number]) {
        loadImportHistory(po_number);
      }
    }
  };

  const toggleImportHistoryExpansion = (po_number: string) => {
    setExpandedImportHistory(prev =>
      prev.includes(po_number)
        ? prev.filter(po => po !== po_number)
        : [...prev, po_number]
    );
  };

  const togglePOLinesExpansion = (po_number: string) => {
    setExpandedPOLines(prev =>
      prev.includes(po_number)
        ? prev.filter(po => po !== po_number)
        : [...prev, po_number]
    );
  };

  const toggleAlertsExpansion = (po_number: string) => {
    setExpandedAlerts(prev =>
      prev.includes(po_number)
        ? prev.filter(po => po !== po_number)
        : [...prev, po_number]
    );
    // Load alerts when expanding
    if (!expandedAlerts.includes(po_number)) {
      loadPOAlerts(po_number);
    }
  };

  const handleScanAlerts = async () => {
    try {
      setScanningAlerts(true);
      const result = await generateAlerts();

      if (result.success) {
        const totalNew = result.alerts_created || 0;
        const totalResolved = result.alerts_resolved || 0;

        const messages = [];
        if (totalNew > 0) {
          messages.push(`${totalNew} new alert${totalNew > 1 ? 's' : ''} found`);
        }
        if (totalResolved > 0) {
          messages.push(`${totalResolved} alert${totalResolved > 1 ? 's' : ''} auto-resolved`);
        }

        if (messages.length > 0) {
          setError(`✓ ${messages.join(', ')}`);
        } else {
          setError('✓ No new alerts found');
        }

        // Reload alerts to show updated state
        await loadAlertsSummary();
      } else {
        setError('Failed to scan for alerts');
      }
    } catch (err: any) {
      setError(`Error scanning alerts: ${err.message}`);
    } finally {
      setScanningAlerts(false);
    }
  };

  const clearAllPriorities = async () => {
    try {
      await api.delete(`/containers-priorities`);
      loadData();
    } catch (err) {
      setError('Error clearing priorities');
    }
  };

  const handleEditDelivery = (container: Container) => {
    setEditingContainer(container.id);
    setEditDeliveryData({
      delivery_day: container.delivery_day || '',
      delivery_time: container.delivery_time || '',
      seal_no: container.seal_no || '',
      dock: container.dock || '',
      chain_of_custody: container.chain_of_custody || ''
    });
  };

  const handleSaveDelivery = async (containerId: number) => {
    try {
      // Optimistically update local state immediately
      const updateContainer = (container: Container) => {
        if (container.id === containerId) {
          return {
            ...container,
            delivery_day: editDeliveryData.delivery_day || container.delivery_day,
            delivery_time: editDeliveryData.delivery_time || container.delivery_time,
            seal_no: editDeliveryData.seal_no || container.seal_no,
            dock: editDeliveryData.dock || container.dock,
            chain_of_custody: editDeliveryData.chain_of_custody || container.chain_of_custody
          };
        }
        return container;
      };

      setPOGroups(prevGroups =>
        prevGroups.map(po => ({
          ...po,
          priority_containers: po.priority_containers.map(updateContainer),
          standard_containers: po.standard_containers.map(updateContainer)
        }))
      );

      setEditingContainer(null);

      // Update backend
      await api.put(`/containers/${containerId}`, {
        delivery_day: editDeliveryData.delivery_day || null,
        delivery_time: editDeliveryData.delivery_time || null,
        seal_no: editDeliveryData.seal_no || null,
        dock: editDeliveryData.dock || null,
        chain_of_custody: editDeliveryData.chain_of_custody || null
      });
    } catch (err) {
      setError('Error updating container information');
      console.error('Error updating container:', err);
      // Revert optimistic update on error
      loadData();
    }
  };

  const handleCancelEditDelivery = () => {
    setEditingContainer(null);
    setEditDeliveryData({delivery_day: '', delivery_time: '', seal_no: '', dock: '', chain_of_custody: ''});
  };

  const handleEditDemurrage = (po: POGroup) => {
    setEditingPODemurrage(po.po_number);
    setEditDemurrageData(po.demurrage_deadline || '');
  };

  const handleSaveDemurrage = async (poNumber: string) => {
    try {
      await api.put(`/po/${poNumber}/demurrage`, {
        demurrage_deadline: editDemurrageData || null
      });
      setEditingPODemurrage(null);
      loadData(); // Refresh data
    } catch (err) {
      setError('Error updating demurrage deadline');
    }
  };

  const handleCancelEditDemurrage = () => {
    setEditingPODemurrage(null);
    setEditDemurrageData('');
  };

  const handleDueDiligenceToggle = async (poNumber: string, currentStatus: boolean) => {
    try {
      // Optimistically update local state immediately
      setPOGroups(prevGroups =>
        prevGroups.map(po =>
          po.po_number === poNumber
            ? { ...po, due_diligence_completed: !currentStatus }
            : po
        )
      );

      // Update backend
      await api.put(`/po/${poNumber}/due-diligence`, {
        due_diligence_completed: !currentStatus
      });
    } catch (err) {
      setError('Error updating due diligence status');
      console.error('Error updating due diligence:', err);
      // Revert optimistic update on error
      loadData();
    }
  };

  const jumpToContainer = (container_no: string) => {
    // Find and expand the PO containing this container
    for (const po of poGroups) {
      const hasContainer = [...po.priority_containers, ...po.standard_containers]
        .some(c => c.container_no === container_no);

      if (hasContainer && !expanded.includes(po.po_number)) {
        setExpanded(prev => [...prev, po.po_number]);
        break;
      }
    }

    // Scroll to container (basic implementation)
    setTimeout(() => {
      const element = document.getElementById(`container-${container_no}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setImportFile(file || null);
    setImportResults(null);
  };

  const handleImportOpenOrders = async () => {
    if (!importFile) {
      setError('Please select a file');
      return;
    }

    setImporting(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', importFile);
      // Include staff_no so the import is properly attributed
      if (user?.staff_no) {
        formData.append('staff_no', user.staff_no);
      }

      const response = await api.post(
        '/packing/process-open-orders',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setImportResults(response.data);
      setImportFile(null);

      // Reset file input
      const fileInput = document.getElementById('open-orders-file-input') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }

      // Optionally reload data to show changes
      loadData();

    } catch (error: any) {
      console.error('Import error:', error);
      setError(error.response?.data?.message || 'Failed to import Open Orders file');
    } finally {
      setImporting(false);
    }
  };

  const PRISM_API_URL = process.env.REACT_APP_PRISM_API_URL || 'http://192.168.1.108:5000';

  const handleSyncFromPrism = async () => {
    setSyncing(true);
    setSyncResults(null);
    setSyncError(null);
    setError(null);

    try {
      // Step 1: Fetch open orders directly from Prism API on the local network
      const prismResponse = await fetch(`${PRISM_API_URL}/api/open-orders`, {
        signal: AbortSignal.timeout(30000),
      });

      if (!prismResponse.ok) {
        throw new Error(`Prism API returned ${prismResponse.status}. Is the VM running?`);
      }

      const prismData = await prismResponse.json();
      if (!prismData.success || !prismData.lines?.length) {
        throw new Error('No open order lines returned from Prism');
      }

      // Step 2: POST the lines to Abaris sync endpoint (session auth via axios interceptor)
      const syncResponse = await api.post('/packing/sync-open-orders', {
        lines: prismData.lines,
      }, {
        timeout: 120000,
      });

      setSyncResults(syncResponse.data);
      loadData();

    } catch (error: any) {
      console.error('Prism sync error:', error);
      if (error.name === 'TimeoutError' || error.message?.includes('timeout')) {
        setSyncError('Could not reach Prism API. Make sure you are on the work network.');
      } else if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
        setSyncError('Cannot connect to Prism API. Are you on the work network?');
      } else {
        setSyncError(error.response?.data?.error || error.message || 'Sync failed');
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleUpdatePOStatus = async (po_number: string, new_status: 'active' | 'inactive') => {
    setUpdatingPOStatus(po_number);
    setError(null);

    try {
      const response = await api.post(
        '/update-po-status',
        {
          po_number: po_number,
          status: new_status,
          staff_no: user?.staff_no || 'UNKNOWN'
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        // Reload data to show updated status
        loadData();
      } else {
        setError(`Failed to update PO ${po_number}: ${response.data.error}`);
      }

    } catch (error: any) {
      console.error('PO status update error:', error);
      setError(error.response?.data?.message || `Failed to update PO ${po_number} status`);
    } finally {
      setUpdatingPOStatus(null);
    }
  };

  const handleBulkSealUpdate = async () => {
    if (!sealUpdateText.trim()) {
      setError('Please paste container/seal data');
      return;
    }

    setBulkSealUpdating(true);
    setError(null);
    setBulkSealResults(null);

    try {
      const response = await api.post(
        '/containers/bulk-update-seals',
        {
          text_block: sealUpdateText
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      setBulkSealResults(response.data);

      // Clear textarea on success
      if (response.data.success && response.data.updated_count > 0) {
        setSealUpdateText('');
        // Reload data to show updated seals
        loadData();
      }

    } catch (error: any) {
      console.error('Bulk seal update error:', error);
      setError(error.response?.data?.error || 'Failed to update seal numbers');
      if (error.response?.data?.parse_errors) {
        setBulkSealResults(error.response.data);
      }
    } finally {
      setBulkSealUpdating(false);
    }
  };

  const handleRemoveContainerPreview = async () => {
    if (!removeContainerNo.trim()) {
      setError('Please enter a container number');
      return;
    }

    setRemoveContainerLoading(true);
    setError(null);
    setRemoveContainerPreview(null);

    try {
      const response = await api.post(
        '/remove_container',
        {
          container_no: removeContainerNo.trim(),
          mode: 'preview'
        }
      );

      setRemoveContainerPreview(response.data);

      // If container can be deleted, open confirmation dialog
      if (response.data.success && response.data.can_delete) {
        setRemoveContainerConfirmOpen(true);
      }

    } catch (error: any) {
      console.error('Remove container preview error:', error);
      console.error('Response data:', error.response?.data);
      console.error('Response status:', error.response?.status);

      // If it's a 400 error with response data (blocked container)
      if (error.response?.status === 400 && error.response?.data) {
        const errorData = error.response.data;
        setRemoveContainerPreview(errorData);

        // If requires force confirmation, open the force dialog
        if (errorData.requires_force) {
          setRemoveContainerForceConfirmOpen(true);
        }
      } else {
        setError(error.response?.data?.error || error.response?.data?.message || 'Failed to preview container removal');
      }
    } finally {
      setRemoveContainerLoading(false);
    }
  };

  const handleRemoveContainerExecute = async (force: boolean = false) => {
    setRemoveContainerLoading(true);
    setError(null);

    try {
      const response = await api.post(
        '/remove_container',
        {
          container_no: removeContainerNo.trim(),
          mode: 'execute',
          force: force
        }
      );

      if (response.data.success) {
        const msg = response.data.was_un_receipted
          ? `✓ Container ${removeContainerNo} has been un-receipted and reset to In Transit status. You can now reschedule it for receipt.`
          : `✓ Container ${removeContainerNo} has been reset to In Transit status.`;
        alert(msg);

        // Reset state
        setRemoveContainerNo('');
        setRemoveContainerPreview(null);
        setRemoveContainerConfirmOpen(false);
        setRemoveContainerForceConfirmOpen(false);

        // Reload data to reflect changes
        loadData();
      }

    } catch (error: any) {
      console.error('Remove container execution error:', error);
      setError(error.response?.data?.error || 'Failed to remove container');
      setRemoveContainerForceConfirmOpen(false);
    } finally {
      setRemoveContainerLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Grid container spacing={3}>
        {/* Header with Navigation */}
        <Grid item xs={12}>
          <ContainerNav
            rightContent={
              <Box display="flex" alignItems="center" gap={1}>
                <Tooltip title="Import Open Orders from Prism ERP" arrow>
                  <IconButton
                    onClick={() => setImportModalOpen(true)}
                    sx={{
                      border: '2px solid',
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: 'primary.main',
                        color: 'white'
                      }
                    }}
                    size="medium"
                  >
                    <UploadIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Bulk Update Seal Numbers" arrow>
                  <IconButton
                    onClick={() => setBulkSealModalOpen(true)}
                    sx={{
                      border: '2px solid',
                      borderColor: 'success.main',
                      color: 'success.main',
                      '&:hover': {
                        bgcolor: 'success.main',
                        color: 'white'
                      }
                    }}
                    size="medium"
                  >
                    <LockIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Scan for Alerts" arrow>
                  <IconButton
                    onClick={handleScanAlerts}
                    disabled={scanningAlerts}
                    sx={{
                      border: '2px solid',
                      borderColor: 'error.main',
                      color: 'error.main',
                      '&:hover': {
                        bgcolor: 'error.main',
                        color: 'white'
                      },
                      '&.Mui-disabled': {
                        borderColor: 'grey.300',
                        color: 'grey.300'
                      }
                    }}
                    size="medium"
                  >
                    {scanningAlerts ? <CircularProgress size={20} /> : <AlertIcon />}
                  </IconButton>
                </Tooltip>

                <Tooltip title="Administrator Toolbox - Power User Functions" arrow>
                  <IconButton
                    onClick={() => setAdminModalOpen(true)}
                    sx={{
                      border: '2px solid',
                      borderColor: '#f50057',
                      color: '#f50057',
                      '&:hover': {
                        bgcolor: '#f50057',
                        color: 'white'
                      }
                    }}
                    size="medium"
                  >
                    <AdminIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            }
          />

          {/* Priority Watch List */}
          {priorities.length > 0 && (
            <Paper
              elevation={3}
              sx={{
                p: 2,
                mt: 2,
                maxWidth: 400,
                border: '2px solid #CB6015',
                backgroundColor: '#FFF8F0'
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" color="primary">
                  Priority Watch ({priorities.length})
                </Typography>
                <IconButton size="small" onClick={clearAllPriorities}>
                  <ClearIcon />
                </IconButton>
              </Box>
              {priorities.map((priority) => (
                <Box
                  key={priority.priority_id}
                  sx={{
                    mb: 1,
                    p: 1,
                    backgroundColor: 'white',
                    borderRadius: 1,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#f5f5f5' }
                  }}
                  onClick={() => jumpToContainer(priority.container_no)}
                >
                  <Typography variant="body2" fontWeight="bold">
                    {priority.container_no}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    "{priority.comment}"
                  </Typography>
                </Box>
              ))}
            </Paper>
          )}
        </Grid>

        {/* View Toggle - by PO or by Packing List */}
        <Grid item xs={12}>
          <Box display="flex" gap={1}>
            <Button
              variant={viewMode === 'by-po' ? 'contained' : 'outlined'}
              color="primary"
              size="small"
              startIcon={<ReceiptIcon />}
              onClick={() => setViewMode('by-po')}
              sx={{
                textTransform: 'none',
                fontWeight: viewMode === 'by-po' ? 'bold' : 'normal',
                minWidth: 'auto',
                px: 2
              }}
            >
              by PO
            </Button>
            <Button
              variant={viewMode === 'by-packing-list' ? 'contained' : 'outlined'}
              color="primary"
              size="small"
              startIcon={<LocalShippingIcon />}
              onClick={() => setViewMode('by-packing-list')}
              sx={{
                textTransform: 'none',
                fontWeight: viewMode === 'by-packing-list' ? 'bold' : 'normal',
                minWidth: 'auto',
                px: 2
              }}
            >
              by Packing List
            </Button>
          </Box>
        </Grid>

        {/* Search and Filters - only shown for PO view */}
        {viewMode === 'by-po' && (
        <Grid item xs={12}>
            <Card>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      placeholder="Search by PO number or stock code... (Press Enter or click away)"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onBlur={() => setAppliedSearchTerm(searchTerm)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          setAppliedSearchTerm(searchTerm);
                        }
                      }}
                      InputProps={{
                        startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth>
                      <InputLabel>Date Filter</InputLabel>
                      <Select
                        value={filters.date}
                        onChange={(e) => setFilters({ ...filters, date: e.target.value })}
                      >
                        <MenuItem value="">All Dates</MenuItem>
                        <MenuItem value="today">Today</MenuItem>
                        <MenuItem value="next_week">Next Week</MenuItem>
                        <MenuItem value="next_two_weeks">Next Two Weeks</MenuItem>
                        <MenuItem value="next_month">Next Month</MenuItem>
                        <MenuItem value="next_two_months">Next Two Months</MenuItem>
                        <MenuItem value="next_three_months">Next Three Months</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth>
                      <InputLabel>Supplier</InputLabel>
                      <Select
                        value={filters.supplier}
                        onChange={(e) => setFilters({ ...filters, supplier: e.target.value })}
                      >
                        <MenuItem value="">All Suppliers</MenuItem>
                        {supplierCodes.map((supplier) => (
                          <MenuItem key={supplier} value={supplier}>{supplier}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3} display="flex" alignItems="center" gap={1}>
                        <Chip
                          label={`Total: ${poSummaryStats.total}`}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                        <Chip
                          label={`No Packing List: ${poSummaryStats.noPackingList}`}
                          size="small"
                          sx={{
                            backgroundColor: '#9c27b0',
                            color: 'white',
                            fontWeight: 500
                          }}
                        />
                        <Chip
                          label={`Mismatch: ${poSummaryStats.mismatch}`}
                          size="small"
                          color="warning"
                          sx={{ fontWeight: 500 }}
                        />
                        <Chip
                          label={`Match: ${poSummaryStats.match}`}
                          size="small"
                          color="success"
                          sx={{ fontWeight: 500 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={2} display="flex" justifyContent="flex-end">
                        <Button
                          variant="contained"
                          startIcon={<UploadIcon />}
                          onClick={() => setUploadModalOpen(true)}
                        >
                          Upload Packing List
                        </Button>
                      </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Error Alert */}
        {error && (
          <Grid item xs={12}>
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Grid>
        )}

        {/* Main View - Toggle between PO Reconciliation and Packing List views */}
        <Grid item xs={12}>
          {viewMode === 'by-po' ? (
            <POReconciliation
              searchTerm={appliedSearchTerm}
              dateFilter={filters.date}
              supplierFilter={filters.supplier}
              onDataLoad={handlePODataLoad}
            />
          ) : (
            <ContainerDashboard
              onNavigateToPO={(poNumber) => {
                setAppliedSearchTerm(poNumber);
                setSearchTerm(poNumber);
                setViewMode('by-po');
              }}
            />
          )}
        </Grid>
      </Grid>

      {/* Container Detail Modal */}
      <ContainerDetailModal
        open={detailModalOpen}
        container={selectedContainerForDetail}
        onClose={() => {
          setDetailModalOpen(false);
          setSelectedContainerForDetail(null);
        }}
        onSaved={() => {
          // Reload grouped containers to reflect changes
          loadGroupedContainers();
        }}
      />

      {/* Packing List Upload Modal */}
      <PackingListUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onSuccess={() => {
          // Reload data after successful upload
          loadGroupedContainers();
        }}
      />

      {/* Manual Container Dialog */}
      <ManualContainerDialog
        open={manualContainerDialogOpen}
        onClose={() => setManualContainerDialogOpen(false)}
        onSuccess={() => loadGroupedContainers()}
        staffNo={user?.staff_no}
      />

      {/* Priority Comment Modal */}
      <Dialog open={priorityModalOpen} onClose={() => setPriorityModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add Priority: {selectedContainer?.container_no}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Why is this container priority?"
            placeholder="e.g., Customer urgent - Job 123, Demurrage risk, etc."
            value={priorityComment}
            onChange={(e) => setPriorityComment(e.target.value)}
            sx={{ mt: 2 }}
            inputProps={{ maxLength: 255 }}
            helperText={`${priorityComment.length}/255 characters`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPriorityModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handlePrioritySubmit}
            variant="contained"
            disabled={!priorityComment.trim()}
          >
            Add Priority
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Container Confirmation Dialog */}
      <Dialog
        open={removeContainerConfirmOpen}
        onClose={() => setRemoveContainerConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#ffebee', color: '#d32f2f' }}>
          ⚠️ Confirm Container Removal
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              This action will permanently delete:
            </Typography>
          </Alert>

          {removeContainerPreview && (
            <Box sx={{ pl: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • Container: <strong>{removeContainerNo}</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • PO: <strong>{removeContainerPreview.po_number}</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • Status: <strong>{removeContainerPreview.status}</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • <strong>{removeContainerPreview.container_lines_count || 0}</strong> container line item(s)
              </Typography>
              {removeContainerPreview.packing_list_lines_count > 0 && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • <strong>{removeContainerPreview.packing_list_lines_count}</strong> packing list line(s)
                </Typography>
              )}
              {removeContainerPreview.stock_conversions_count > 0 && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • <strong>{removeContainerPreview.stock_conversions_count}</strong> stock code conversion(s)
                </Typography>
              )}
            </Box>
          )}

          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>This action cannot be undone.</strong>
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRemoveContainerConfirmOpen(false);
              setRemoveContainerPreview(null);
            }}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleRemoveContainerExecute(false)}
            variant="contained"
            color="error"
            disabled={removeContainerLoading}
            startIcon={removeContainerLoading ? <CircularProgress size={16} /> : <ClearIcon />}
          >
            {removeContainerLoading ? 'Deleting...' : 'Confirm Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Force Delete Confirmation Dialog (for receipted containers) */}
      <Dialog
        open={removeContainerForceConfirmOpen}
        onClose={() => setRemoveContainerForceConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#d32f2f', color: 'white' }}>
          🔄 Un-Receipt Container
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              ⚠️ This container has already been RECEIPTED!
            </Typography>
          </Alert>

          {removeContainerPreview && (
            <Box sx={{ pl: 2, mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • Container: <strong>{removeContainerNo}</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • PO: <strong>{removeContainerPreview.po_number}</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'error.main', fontWeight: 600 }}>
                • Status: <strong>{removeContainerPreview.status}</strong>
              </Typography>
            </Box>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              This operation will:
            </Typography>
            <Typography variant="body2" component="div">
              • Delete all receipt records and photos
              <br />
              • Reset container status to "In Transit"
              <br />
              • Clear delivery schedule (date, time, dock)
              <br />
              • Keep container lines (packing list data)
              <br />
              • Keep chain of custody information
              <br />
              <br />
              <strong>The container will be available to reschedule for receipt.</strong>
            </Typography>
          </Alert>

          <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
            Confirm you want to un-receipt this container?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRemoveContainerForceConfirmOpen(false);
              setRemoveContainerPreview(null);
            }}
            color="primary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleRemoveContainerExecute(true)}
            variant="contained"
            sx={{ backgroundColor: '#f57c00', '&:hover': { backgroundColor: '#e65100' } }}
            disabled={removeContainerLoading}
            startIcon={removeContainerLoading ? <CircularProgress size={16} /> : <ClearIcon />}
          >
            {removeContainerLoading ? 'Un-receipting...' : 'Confirm Un-Receipt'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Open Order Import Modal */}
      <Dialog open={importModalOpen} onClose={() => setImportModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          📊 Open Order Import
        </DialogTitle>
        <DialogContent>
          {/* Sync from Prism — direct API pull (requires work network) */}
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Sync from Prism</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pull the latest open orders directly from Prism. Requires work network connection.
          </Typography>

          <Button
            variant="contained"
            onClick={handleSyncFromPrism}
            disabled={syncing}
            startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
            fullWidth
          >
            {syncing ? 'Syncing from Prism...' : 'Sync from Prism'}
          </Button>

          {syncResults && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2">Sync completed successfully!</Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" display="block">
                  Received: {syncResults.total_lines_received} lines from Prism
                </Typography>
                <Typography variant="caption" display="block">
                  Added/Updated: {syncResults.imported_count} records
                </Typography>
                {syncResults.marked_for_review > 0 && (
                  <Typography variant="caption" display="block">
                    Marked for review: {syncResults.marked_for_review} lines no longer in Prism
                  </Typography>
                )}
                {syncResults.pending_review_count > 0 && (
                  <Typography variant="caption" display="block">
                    Pending review: {syncResults.pending_review_count} POs need operator confirmation
                  </Typography>
                )}
              </Box>
            </Alert>
          )}

          {syncError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {syncError}
            </Alert>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Manual file upload — fallback */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Manual File Upload</Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Upload an Open Order report exported from Prism ERP.
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic', mb: 2 }}>
            Use Prism report code: <strong>A_PO_DEL_SCHED_CUST2</strong>
          </Typography>

          <Box sx={{ mt: 2 }}>
            <Stack spacing={2}>
              <input
                id="open-orders-file-input"
                type="file"
                accept=".xlsx,.xls,.xlsm,.csv,.xml,.htm,.html"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <label htmlFor="open-orders-file-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  fullWidth
                >
                  Select Open Orders File
                </Button>
              </label>

              {importFile && (
                <Typography variant="body2" noWrap>
                  {importFile.name}
                </Typography>
              )}

              {importResults && (
                <Alert severity="success">
                  <Typography variant="body2">Import completed successfully!</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" display="block">
                      Processed: {importResults.total_rows_processed} records
                    </Typography>
                    <Typography variant="caption" display="block">
                      Added/Updated: {importResults.actual_db_count} records
                    </Typography>
                    {importResults.pending_review_count > 0 && (
                      <Typography variant="caption" display="block">
                        Pending Review: {importResults.pending_review_count} POs need operator confirmation
                      </Typography>
                    )}
                  </Box>
                </Alert>
              )}
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportModalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleImportOpenOrders}
            disabled={!importFile || importing}
            startIcon={importing ? <CircularProgress size={20} /> : <DownloadIcon />}
          >
            {importing ? 'Importing...' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Seal Update Modal */}
      <Dialog open={bulkSealModalOpen} onClose={() => setBulkSealModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          🔒 Bulk Seal Number Update
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 1, mb: 2 }}>
            Paste container and seal number data from freight agent emails.
          </Typography>

          <Stack spacing={2}>
            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder={"Paste container/seal data here...\nSupports both single-line and multi-line block formats.\n\nSingle-line example:\nTRHU4061678 ML-CL0502288 40 DRY 9'6 64 PACKAGES\nTIIU4737979 ML-CL0502585 40 DRY 9'6 63 PACKAGES\n\nMulti-line block example:\nHPCU2641502 21,399.000 kgs.\n004071\nMEDU5668171 22,100.000 kgs.\n004072"}
              value={sealUpdateText}
              onChange={(e) => setSealUpdateText(e.target.value)}
              variant="outlined"
              helperText={`${sealUpdateText.split('\n').filter(l => l.trim()).length} lines`}
            />

            {bulkSealResults && (
              bulkSealResults.success ? (
                <Alert severity="success">
                  <Typography variant="body2" fontWeight="bold">Bulk update completed!</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" display="block">
                      ✅ Updated: {bulkSealResults.updated_count} containers
                    </Typography>
                    {bulkSealResults.not_found_count > 0 && (
                      <Typography variant="caption" display="block" color="warning.main">
                        ⚠️ Not found: {bulkSealResults.not_found_count} containers
                      </Typography>
                    )}
                  </Box>
                </Alert>
              ) : (
                <Alert severity="error">
                  <Typography variant="body2">
                    {bulkSealResults.error || 'Failed to update seal numbers'}
                  </Typography>
                </Alert>
              )
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkSealModalOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={handleBulkSealUpdate}
            disabled={!sealUpdateText.trim() || bulkSealUpdating}
            startIcon={bulkSealUpdating ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {bulkSealUpdating ? 'Updating...' : 'Update Seal Numbers'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Administrator Toolbox Modal */}
      <Dialog open={adminModalOpen} onClose={() => setAdminModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#fff0f5', color: '#f50057', display: 'flex', alignItems: 'center', gap: 1 }}>
          <AdminIcon />
          Administrator Toolbox
          <Chip
            label="ADMIN"
            size="small"
            sx={{
              backgroundColor: '#f50057',
              color: 'white',
              fontSize: '0.65rem',
              height: '18px',
              fontWeight: 'bold',
              ml: 1
            }}
          />
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 2, mb: 3 }}>
            <Typography variant="body2" fontWeight="bold">Power user functions - use with caution</Typography>
          </Alert>

          {/* Import History Navigation */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Import History
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              View history of packing list and open order imports
            </Typography>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => {
                setAdminModalOpen(false);
                navigate('/containers/import-history');
              }}
              fullWidth
            >
              View Import History
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Manual Container Creation */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Manual Container Creation
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Create a container with packing list data for one-off deliveries
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ContainerIcon />}
              onClick={() => {
                setAdminModalOpen(false);
                setManualContainerDialogOpen(true);
              }}
              fullWidth
            >
              Create Manual Container
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Remove Container Function */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Remove Container
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Delete a specific container and its associated data
            </Typography>
            <Stack spacing={2}>
              <TextField
                size="small"
                fullWidth
                placeholder="Enter container number (e.g., ABCD1234567)"
                value={removeContainerNo}
                onChange={(e) => setRemoveContainerNo(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleRemoveContainerPreview();
                  }
                }}
              />
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={handleRemoveContainerPreview}
                disabled={!removeContainerNo.trim() || removeContainerLoading}
                startIcon={removeContainerLoading ? <CircularProgress size={16} /> : <SearchIcon />}
              >
                {removeContainerLoading ? 'Searching...' : 'Search & Preview'}
              </Button>

              {/* Preview Results */}
              {removeContainerPreview && !removeContainerConfirmOpen && (
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }} elevation={0}>
                  {removeContainerPreview.success ? (
                    <>
                      <Typography variant="caption" display="block" sx={{ fontWeight: 600, mb: 1 }}>
                        Preview Results for Container {removeContainerNo}:
                      </Typography>
                      <Typography variant="caption" display="block">
                        • PO Number: {removeContainerPreview.po_number || 'N/A'}
                      </Typography>
                      <Typography variant="caption" display="block">
                        • Status: {removeContainerPreview.status || 'Unknown'}
                      </Typography>
                      <Typography variant="caption" display="block">
                        • Container lines: {removeContainerPreview.container_lines_count || 0}
                      </Typography>
                      {!removeContainerPreview.can_delete && (
                        <Alert severity="error" sx={{ mt: 1, fontSize: '0.75rem' }}>
                          <Typography variant="caption" display="block" sx={{ fontWeight: 600 }}>
                            ⚠️ Cannot delete - Container status is '{removeContainerPreview.status}'
                          </Typography>
                          <Typography variant="caption" display="block">
                            Only containers with status 'In Transit' can be deleted.
                          </Typography>
                        </Alert>
                      )}
                    </>
                  ) : (
                    <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
                      {removeContainerPreview.message || 'Container not found'}
                    </Alert>
                  )}
                </Paper>
              )}
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Import Management Modal */}
      <Dialog open={importManagementModalOpen} onClose={() => setImportManagementModalOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f0f4ff', display: 'flex', alignItems: 'center', gap: 1 }}>
          <UploadIcon />
          Manage Packing Lists - PO {selectedPOForImports}
        </DialogTitle>
        <DialogContent>
          {/* Upload Section */}
          <Paper sx={{ p: 3, mt: 2, mb: 3, bgcolor: '#f8f9fa' }} elevation={0}>
            <Typography variant="h6" gutterBottom>
              Upload Packing List
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload new packing lists to add containers or update existing ones
            </Typography>
            <input
              accept=".xlsx,.xls,.xlsm,.xml,.pdf"
              style={{ display: 'none' }}
              id="modal-packing-upload"
              type="file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file && selectedPOForImports) {
                  handleAdditionalPackingListUpload(selectedPOForImports, file);
                }
              }}
            />
            <label htmlFor="modal-packing-upload">
              <Button
                variant="contained"
                component="span"
                startIcon={uploadingAdditionalPackingList === selectedPOForImports ? <CircularProgress size={20} /> : <UploadIcon />}
                disabled={uploadingAdditionalPackingList === selectedPOForImports}
                size="large"
              >
                {uploadingAdditionalPackingList === selectedPOForImports ? 'Uploading...' : 'Select Packing List File'}
              </Button>
            </label>
          </Paper>

          {/* Import History Section */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Import History
          </Typography>
          {selectedPOForImports && loadingImportHistory[selectedPOForImports] ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : selectedPOForImports && importHistory[selectedPOForImports] &&
             (importHistory[selectedPOForImports].open_order_imports.length > 0 ||
              importHistory[selectedPOForImports].packing_list_imports.length > 0) ? (
            <Box>
              {/* Open Order Imports */}
              {importHistory[selectedPOForImports].open_order_imports.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                    Open Order Imports
                  </Typography>
                  <TableContainer component={Paper} elevation={0}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell><strong>Filename</strong></TableCell>
                          <TableCell><strong>Import Date</strong></TableCell>
                          <TableCell align="right"><strong>Lines Imported</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {importHistory[selectedPOForImports].open_order_imports.map((imp, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell>{imp.filename}</TableCell>
                            <TableCell>
                              {new Date(imp.import_date).toLocaleString('en-AU', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </TableCell>
                            <TableCell align="right">{imp.lines_count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Packing List Imports */}
              {importHistory[selectedPOForImports].packing_list_imports.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                    Packing List Imports
                  </Typography>
                  <TableContainer component={Paper} elevation={0}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell><strong>Filename</strong></TableCell>
                          <TableCell><strong>Import Date</strong></TableCell>
                          <TableCell><strong>Container</strong></TableCell>
                          <TableCell align="right"><strong>Lines</strong></TableCell>
                          <TableCell align="right"><strong>Matched</strong></TableCell>
                          <TableCell align="right"><strong>Unmatched</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {importHistory[selectedPOForImports].packing_list_imports.map((imp, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell>{imp.filename}</TableCell>
                            <TableCell>
                              {new Date(imp.import_date).toLocaleString('en-AU', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </TableCell>
                            <TableCell>{imp.container_no || '-'}</TableCell>
                            <TableCell align="right">{imp.lines_count || 0}</TableCell>
                            <TableCell align="right">{imp.lines_matched || 0}</TableCell>
                            <TableCell align="right">{imp.lines_unmatched || 0}</TableCell>
                            <TableCell>
                              <Chip
                                label={imp.processing_status || 'unknown'}
                                size="small"
                                color={
                                  imp.processing_status === 'completed' ? 'success' :
                                  imp.processing_status === 'failed' ? 'error' :
                                  imp.processing_status === 'processing' ? 'warning' :
                                  'default'
                                }
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          ) : (
            <Alert severity="info">
              No import history found for this Purchase Order
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportManagementModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Alerts Modal */}
      <Dialog open={alertsModalOpen} onClose={() => setAlertsModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#ffebee', color: '#d32f2f', display: 'flex', alignItems: 'center', gap: 1 }}>
          <AlertIcon />
          Active Alerts for PO {selectedPOForAlerts}
          {selectedPOForAlerts && (
            <Chip
              label={`${getPOAlertCount(selectedPOForAlerts)} alert${getPOAlertCount(selectedPOForAlerts) > 1 ? 's' : ''}`}
              size="small"
              sx={{
                bgcolor: '#d32f2f',
                color: 'white',
                ml: 1
              }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {selectedPOForAlerts && poAlerts[selectedPOForAlerts] && poAlerts[selectedPOForAlerts].length > 0 ? (
            <TableContainer component={Paper} sx={{ mt: 2 }} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Severity</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Alert Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {poAlerts[selectedPOForAlerts].map((alert) => (
                    <TableRow key={alert.id} hover>
                      <TableCell>
                        <Chip
                          label={alert.severity.toUpperCase()}
                          size="small"
                          color={alert.severity === 'critical' ? 'error' : alert.severity === 'high' ? 'warning' : 'info'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {alert.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {alert.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {alert.metadata && (
                          <Typography variant="caption" color="text.secondary">
                            {alert.alert_type === 'quantity_mismatch' && alert.metadata.stock_code && (
                              <>Stock: {alert.metadata.stock_code}<br />Variance: {alert.metadata.variance} units</>
                            )}
                            {alert.alert_type === 'delivery_overdue' && alert.metadata.container_no && (
                              <>Container: {alert.metadata.container_no}<br />Days until expected: {alert.metadata.days_until_expected}</>
                            )}
                            {alert.alert_type === 'container_seal_missing' && alert.metadata.container_no && (
                              <>Container: {alert.metadata.container_no}</>
                            )}
                            {alert.alert_type === 'missing_stock_match' && alert.metadata.stock_code && (
                              <>Stock: {alert.metadata.stock_code}</>
                            )}
                            {alert.alert_type === 'container_photo_missing' && alert.metadata.container_no && (
                              <>Container: {alert.metadata.container_no}</>
                            )}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No alerts found for this PO
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlertsModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Footer with Supported Suppliers */}
      <Box
        sx={{
          mt: 4,
          pt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Supported suppliers:
        </Typography>
        <Chip label="AVDEUS" size="small" color="success" sx={{ fontSize: '0.7rem', height: '20px' }} />
        <Chip label="B&B" size="small" color="success" sx={{ fontSize: '0.7rem', height: '20px' }} />
        <Chip label="CMPC" size="small" color="success" sx={{ fontSize: '0.7rem', height: '20px' }} />
        <Chip label="EKM" size="small" color="success" sx={{ fontSize: '0.7rem', height: '20px' }} />
        <Chip label="HANSOL" size="small" color="success" sx={{ fontSize: '0.7rem', height: '20px' }} />
        <Chip label="JACK" size="small" color="success" sx={{ fontSize: '0.7rem', height: '20px' }} />
        <Chip label="METSA" size="small" color="success" sx={{ fontSize: '0.7rem', height: '20px' }} />
        <Chip label="MOSSPA" size="small" color="success" sx={{ fontSize: '0.7rem', height: '20px' }} />
        <Chip label="NIPDYN" size="small" color="success" sx={{ fontSize: '0.7rem', height: '20px' }} />
        <Chip label="POT" size="small" color="success" sx={{ fontSize: '0.7rem', height: '20px' }} />
        <Chip label="SAPPI" size="small" color="success" sx={{ fontSize: '0.7rem', height: '20px' }} />
        <Chip label="STORA" size="small" color="success" sx={{ fontSize: '0.7rem', height: '20px' }} />
        <Chip label="WHAK" size="small" color="success" sx={{ fontSize: '0.7rem', height: '20px' }} />
      </Box>
    </Box>
  );
};

// Container Card Component
interface ContainerCardProps {
  container: Container;
  currentPoNumber: string;  // The PO section this container is displayed under
  onPriorityToggle: (container: Container) => void;
  getDockDisplayName: (dock: string) => string;
  getStatusColor: (status?: string) => 'default' | 'primary' | 'secondary' | 'success';
  isExpanded: boolean;
  onExpand: (containerId: number) => void;
  containerLines: ContainerLine[];
  editingContainer: number | null;
  editDeliveryData: {delivery_day: string, delivery_time: string, seal_no: string, dock: string, chain_of_custody: string};
  onEditDelivery: (container: Container) => void;
  onSaveDelivery: (containerId: number) => void;
  onCancelEditDelivery: () => void;
  onEditDeliveryDataChange: (data: {delivery_day: string, delivery_time: string, seal_no: string, dock: string, chain_of_custody: string}) => void;
  dockOptions: Array<{value: string, label: string}>;
}

const ContainerCard: React.FC<ContainerCardProps> = ({
  container,
  currentPoNumber,
  onPriorityToggle,
  getDockDisplayName,
  getStatusColor,
  isExpanded,
  onExpand,
  containerLines,
  editingContainer,
  editDeliveryData,
  onEditDelivery,
  onSaveDelivery,
  onCancelEditDelivery,
  onEditDeliveryDataChange,
  dockOptions
}) => {
  // Calculate line count display for multi-PO containers
  const linesForThisPO = container.line_count || containerLines.length;
  const totalLines = container.po_summary?.reduce((sum, po) => sum + po.line_count, 0) || linesForThisPO;
  const otherPOs = container.po_summary?.filter(po => po.po_number !== currentPoNumber) || [];

  // Scroll to another PO section
  const scrollToPO = (poNumber: string) => {
    const poElement = document.getElementById(`po-${poNumber}`);
    if (poElement) {
      poElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  return (
    <Card
      sx={{
        mb: 1,
        border: container.is_priority ? '2px solid #f57c00' : '1px solid #e0e0e0',
        backgroundColor: container.is_priority ? '#fff8e1' : 'white'
      }}
      id={`container-${container.container_no}`}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Main Container Row - Compact Layout */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          {/* Left Section: Priority, Container No, Status */}
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton size="small" onClick={() => onPriorityToggle(container)}>
              {container.is_priority ? (
                <Tooltip title={container.priority_comment}>
                  <StarIcon color="warning" />
                </Tooltip>
              ) : (
                <StarBorderIcon />
              )}
            </IconButton>

            <Typography variant="h6" sx={{ minWidth: '120px' }}>
              {container.container_no}
            </Typography>

            <Chip
              label={container.status}
              color={getStatusColor(container.status)}
              size="small"
            />

            {/* Multi-PO indicator icon */}
            {container.is_shared && (
              <Tooltip title={`This container contains stock from ${container.po_numbers?.length || 0} different POs`}>
                <Chip
                  icon={<CallSplitIcon />}
                  label="Multi-PO"
                  size="small"
                  color="info"
                  variant="outlined"
                />
              </Tooltip>
            )}
          </Box>

          {/* Center Section: Stock Codes, Line Count, and Delivery Details */}
          <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1, ml: 2, mr: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0
            }}>
              {container.stock_codes_preview || 'No stock codes'}
            </Typography>

            {/* Line count - show per-PO breakdown for multi-PO containers */}
            <Chip
              label={container.is_shared
                ? `${linesForThisPO} lines for this PO (${totalLines} total)`
                : `${linesForThisPO} lines`
              }
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.75rem' }}
            />

            {/* Delivery details inline */}
            <Typography
              variant="caption"
              sx={{
                mx: 1,
                color: container.delivery_day ? 'text.secondary' : 'error.main',
                fontWeight: container.delivery_day ? 'normal' : 'bold'
              }}
            >
              {container.delivery_day ? new Date(container.delivery_day).toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: '2-digit' }) : 'No date'}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                mx: 1,
                color: container.seal_no ? 'text.secondary' : 'error.main',
                fontWeight: container.seal_no ? 'normal' : 'bold'
              }}
            >
              Seal: {container.seal_no || 'TBD'}
            </Typography>
          </Box>

          {/* Right Section: Dock and Actions */}
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              label={container.dock ? getDockDisplayName(container.dock) : 'No Dock'}
              size="small"
              color={container.dock ? 'default' : 'warning'}
            />
            <IconButton
              size="small"
              onClick={() => onEditDelivery(container)}
              title="Edit delivery details"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <Button
              size="small"
              onClick={() => onExpand(container.id)}
              endIcon={<ExpandMoreIcon sx={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />}
            >
              {isExpanded ? 'Hide' : 'Details'}
            </Button>
          </Box>
        </Box>

        {/* Priority Comment Row - Only if priority */}
        {container.is_priority && (
          <Box mb={1}>
            <Typography variant="caption" color="warning.main" sx={{ fontStyle: 'italic' }}>
              Priority: "{container.priority_comment}"
            </Typography>
          </Box>
        )}

        {/* Multi-PO badges - Show other POs in this container */}
        {container.is_shared && otherPOs.length > 0 && (
          <Box mb={1} display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="caption" color="text.secondary">
              Also contains:
            </Typography>
            {otherPOs.map((po) => (
              <Chip
                key={po.po_number}
                label={`PO ${po.po_number} (${po.line_count} lines)`}
                size="small"
                color="primary"
                variant="outlined"
                onClick={() => scrollToPO(po.po_number)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    color: 'white'
                  }
                }}
              />
            ))}
          </Box>
        )}

        {/* Delivery Editing Interface */}
        {editingContainer === container.id && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Edit Delivery Details</Typography>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  label="Delivery Date"
                  type="date"
                  size="small"
                  value={editDeliveryData.delivery_day}
                  onChange={(e) => onEditDeliveryDataChange({
                    ...editDeliveryData,
                    delivery_day: e.target.value
                  })}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Delivery Time"
                  type="time"
                  size="small"
                  value={editDeliveryData.delivery_time}
                  onChange={(e) => onEditDeliveryDataChange({
                    ...editDeliveryData,
                    delivery_time: e.target.value
                  })}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  label="Seal Number"
                  size="small"
                  value={editDeliveryData.seal_no}
                  onChange={(e) => onEditDeliveryDataChange({
                    ...editDeliveryData,
                    seal_no: e.target.value
                  })}
                  placeholder="Enter seal number"
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Dock</InputLabel>
                  <Select
                    value={editDeliveryData.dock}
                    onChange={(e) => onEditDeliveryDataChange({
                      ...editDeliveryData,
                      dock: e.target.value
                    })}
                    label="Dock"
                  >
                    <MenuItem value="">
                      <em>Not allocated</em>
                    </MenuItem>
                    {dockOptions.map((dock) => (
                      <MenuItem key={dock.value} value={dock.value}>
                        {dock.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Chain of Custody</InputLabel>
                  <Select
                    value={editDeliveryData.chain_of_custody}
                    onChange={(e) => onEditDeliveryDataChange({
                      ...editDeliveryData,
                      chain_of_custody: e.target.value
                    })}
                    label="Chain of Custody"
                  >
                    <MenuItem value="">
                      <em>Not checked</em>
                    </MenuItem>
                    <MenuItem value="None">None</MenuItem>
                    <MenuItem value="FSC 100%">FSC 100%</MenuItem>
                    <MenuItem value="FSC 70%">FSC 70%</MenuItem>
                    <MenuItem value="FSC Mix Credit">FSC Mix Credit</MenuItem>
                    <MenuItem value="FSC Recycled">FSC Recycled</MenuItem>
                    <MenuItem value="PEFC 100%">PEFC 100%</MenuItem>
                  </Select>
                </FormControl>
                <IconButton
                  color="primary"
                  onClick={() => onSaveDelivery(container.id)}
                  size="small"
                >
                  <SaveIcon />
                </IconButton>
                <IconButton
                  color="secondary"
                  onClick={onCancelEditDelivery}
                  size="small"
                >
                  <CancelIcon />
                </IconButton>
              </Stack>
            </Stack>
          </Box>
        )}
      </CardContent>

      <Collapse in={isExpanded}>
        <CardContent sx={{ pt: 0 }}>
          <Typography variant="h6" gutterBottom>
            Stock Codes & Quantities
          </Typography>
          {containerLines.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Stock Code</strong></TableCell>
                    <TableCell align="right"><strong>Quantity</strong></TableCell>
                    <TableCell align="right"><strong>Count</strong></TableCell>
                    <TableCell align="right"><strong>Weight (Tonnes)</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    const grouped = containerLines.reduce((acc: Record<string, { totalSheets: number, totalMetres: number, palletCount: number, reelCount: number, totalWeight: number, reelWeightKg: number }>, line) => {
                      if (!acc[line.stock_code]) {
                        acc[line.stock_code] = { totalSheets: 0, totalMetres: 0, palletCount: 0, reelCount: 0, totalWeight: 0, reelWeightKg: 0 };
                      }

                      // Each line is either a pallet or a reel
                      // Reels have sheets === 0 (set during import)
                      const isReel = line.sheets === 0 && (line.rolls_per_pallet > 0 || line.metres > 0);

                      if (isReel) {
                        acc[line.stock_code].reelCount += 1;
                        acc[line.stock_code].totalMetres += line.metres || 0;
                        acc[line.stock_code].reelWeightKg += (line.net_weight_tonnes || 0) * 1000;
                      } else {
                        acc[line.stock_code].palletCount += 1;
                        acc[line.stock_code].totalSheets += line.sheets || 0;
                      }

                      acc[line.stock_code].totalWeight += line.net_weight_tonnes || 0;
                      return acc;
                    }, {});

                    const groupedEntries = Object.entries(grouped);

                    const containerTotals = groupedEntries.reduce((totals, [_, data]) => ({
                      totalSheets: totals.totalSheets + data.totalSheets,
                      totalMetres: totals.totalMetres + data.totalMetres,
                      totalPallets: totals.totalPallets + data.palletCount,
                      totalReels: totals.totalReels + data.reelCount,
                      totalWeight: totals.totalWeight + data.totalWeight,
                      totalReelWeightKg: totals.totalReelWeightKg + data.reelWeightKg
                    }), { totalSheets: 0, totalMetres: 0, totalPallets: 0, totalReels: 0, totalWeight: 0, totalReelWeightKg: 0 });

                    return (
                      <>
                        {groupedEntries.map(([stockCode, data]) => {
                          const totalCount = data.palletCount + data.reelCount;
                          const countLabel = data.palletCount > 0 && data.reelCount > 0
                            ? `${data.palletCount} pallets, ${data.reelCount} reels`
                            : data.reelCount > 0
                              ? `${data.reelCount} reels`
                              : `${data.palletCount} pallets`;

                          return (
                            <TableRow key={stockCode}>
                              <TableCell>{stockCode}</TableCell>
                              <TableCell align="right">
                                {data.totalSheets > 0
                                  ? `${data.totalSheets.toLocaleString()} sheets`
                                  : data.reelWeightKg > 0
                                    ? `${data.reelWeightKg.toLocaleString()} kg`
                                    : '-'
                                }
                              </TableCell>
                              <TableCell align="right">{countLabel}</TableCell>
                              <TableCell align="right">{data.totalWeight.toFixed(2)}</TableCell>
                            </TableRow>
                          );
                        })}
                        {groupedEntries.length > 1 && (
                          <TableRow sx={{ backgroundColor: '#f5f5f5', borderTop: '2px solid #ddd' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                              {containerTotals.totalSheets > 0 && containerTotals.totalReelWeightKg > 0
                                ? `${containerTotals.totalSheets.toLocaleString()} sheets / ${containerTotals.totalReelWeightKg.toLocaleString()} kg`
                                : containerTotals.totalSheets > 0
                                  ? `${containerTotals.totalSheets.toLocaleString()} sheets`
                                  : containerTotals.totalReelWeightKg > 0
                                    ? `${containerTotals.totalReelWeightKg.toLocaleString()} kg`
                                    : '-'
                              }
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                              {containerTotals.totalPallets > 0 && containerTotals.totalReels > 0
                                ? `${containerTotals.totalPallets} pallets, ${containerTotals.totalReels} reels`
                                : containerTotals.totalReels > 0
                                  ? `${containerTotals.totalReels} reels`
                                  : `${containerTotals.totalPallets} pallets`
                              }
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                              {containerTotals.totalWeight.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    );
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Loading container details...
            </Typography>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default ContainerManagementEnhanced;